import React, { useState, useEffect } from "react";
import { useAuth } from "@/App";
import api from "@/lib/api";
import { Stethoscope, Plus, Search, Phone, Mail, Edit2, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const specialties = [
  "Geriatría", "Clínica Médica", "Cardiología", "Neurología", "Psiquiatría",
  "Traumatología", "Dermatología", "Oftalmología", "Kinesiología",
  "Nutrición", "Endocrinología", "Neumología", "Urología", "Otro"
];

const specColors = {
  "Geriatría": "bg-violet-50 text-violet-700",
  "Cardiología": "bg-rose-50 text-rose-700",
  "Neurología": "bg-sky-50 text-sky-700",
  "Psiquiatría": "bg-fuchsia-50 text-fuchsia-700",
  "Traumatología": "bg-amber-50 text-amber-700",
};

export default function DoctorsPage() {
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "tenant_admin" || user?.permissions?.includes("crear_pacientes");
  const [doctors, setDoctors] = useState([]);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [form, setForm] = useState({ name: "", matricula: "", specialty: "Geriatría", phone: "", email: "", notes: "" });

  const load = async () => {
    try {
      const r = await api.get("/doctors");
      setDoctors(r.data);
    } catch { toast.error("Error al cargar médicos"); }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editDoc) {
        await api.put(`/doctors/${editDoc.id}`, form);
        toast.success("Médico actualizado");
      } else {
        await api.post("/doctors", form);
        toast.success("Médico creado");
      }
      setDialog(false);
      setEditDoc(null);
      setForm({ name: "", matricula: "", specialty: "Geriatría", phone: "", email: "", notes: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleEdit = (doc) => {
    setEditDoc(doc);
    setForm({ name: doc.name, matricula: doc.matricula, specialty: doc.specialty, phone: doc.phone || "", email: doc.email || "", notes: doc.notes || "" });
    setDialog(true);
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/doctors/${docId}`);
      toast.success("Médico desactivado");
      load();
    } catch { toast.error("Error"); }
  };

  const filtered = doctors.filter(d =>
    d.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.specialty?.toLowerCase().includes(search.toLowerCase()) ||
    d.matricula?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="doctors-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">Médicos</h1>
          <p className="text-sm text-slate-400 mt-1">{doctors.length} profesionales registrados</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar médico..."
              className="pl-9 rounded-xl h-10 bg-white border-slate-200 w-64"
              data-testid="doctor-search"
            />
          </div>
          {canManage && (
            <Dialog open={dialog} onOpenChange={v => { setDialog(v); if (!v) { setEditDoc(null); setForm({ name: "", matricula: "", specialty: "Geriatría", phone: "", email: "", notes: "" }); } }}>
              <DialogTrigger asChild>
                <Button className="rounded-xl px-5 h-10 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all text-sm font-semibold" data-testid="add-doctor-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nuevo Médico
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-bold text-slate-900 font-[Manrope]">{editDoc ? "Editar Médico" : "Nuevo Médico"}</DialogTitle>
                  <DialogDescription className="text-slate-400 text-sm">Profesional del equipo médico</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Nombre completo</Label>
                    <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" required data-testid="doctor-name-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="font-medium text-sm text-slate-700">Matrícula</Label>
                      <Input value={form.matricula} onChange={e => setForm({ ...form, matricula: e.target.value })} placeholder="MP-12345" className="rounded-xl h-10 bg-slate-50 border-slate-200" required data-testid="doctor-matricula-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium text-sm text-slate-700">Especialidad</Label>
                      <Select value={form.specialty} onValueChange={v => setForm({ ...form, specialty: v })}>
                        <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="doctor-specialty-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {specialties.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="font-medium text-sm text-slate-700">Teléfono</Label>
                      <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="doctor-phone-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium text-sm text-slate-700">Email</Label>
                      <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="doctor-email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Notas</Label>
                    <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-xl bg-slate-50 min-h-[60px] border-slate-200" data-testid="doctor-notes-input" />
                  </div>
                  <Button type="submit" className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all" data-testid="submit-doctor-btn">
                    {editDoc ? "Guardar Cambios" : "Crear Médico"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Stethoscope className="w-12 h-12 text-slate-200 mx-auto mb-4" strokeWidth={1.2} />
          <p className="text-slate-400 text-sm">{search ? "Sin resultados" : "No hay médicos registrados"}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(doc => (
            <Card key={doc.id} className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`doctor-card-${doc.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                      <Stethoscope className="w-5 h-5 text-violet-600" strokeWidth={1.8} />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-slate-900">{doc.name}</h3>
                      <p className="text-xs text-slate-400 font-mono">{doc.matricula}</p>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(doc)} className="rounded-full w-8 h-8 hover:bg-slate-100" data-testid={`edit-doctor-${doc.id}`}>
                        <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(doc.id)} className="rounded-full w-8 h-8 hover:bg-rose-50 hover:text-rose-500" data-testid={`delete-doctor-${doc.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                <Badge className={`rounded-full text-[10px] font-semibold border-0 px-2.5 mb-3 ${specColors[doc.specialty] || "bg-slate-50 text-slate-600"}`}>
                  {doc.specialty}
                </Badge>
                <div className="space-y-1.5">
                  {doc.phone && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Phone className="w-3 h-3 text-slate-300" /> {doc.phone}
                    </div>
                  )}
                  {doc.email && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Mail className="w-3 h-3 text-slate-300" /> {doc.email}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
